/**
 * Lucky Charm Node Component
 *
 * Node for generating fortune lucky charm images.
 * Receives phrase input from connected Prompt node (like Generate node).
 *
 * Note: Phrase generation (Agent/LLM) should be done via Prompt node.
 * This node focuses solely on image generation from the received phrase.
 */

import { memo } from 'react';
import { type NodeProps } from '@xyflow/react';
import { BaseNode } from './BaseNode';
import { useI18n } from '@/i18n';
import type { NodeData } from '@/types/imageWorkflow';

const LuckyCharmNodeComponent = ({ id, data, selected }: NodeProps) => {
  const { t } = useI18n();
  const nodeData = data as NodeData;

  return (
    <BaseNode id={id} data={nodeData} type="lucky_charm" selected={selected}>
      <div className="space-y-1">
        {/* Input indicator - like Generate node */}
        <div className="text-[10px] text-gray-400">
          <span className="text-amber-600 font-medium">phrase</span> ← Prompt
        </div>

        {/* Optional config info */}
        <div className="flex flex-wrap gap-1">
          {nodeData.emotion_trend && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-600">
              {String(nodeData.emotion_trend)}
            </span>
          )}
          {nodeData.font_id && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
              {t('imageWorkflows.nodes.luckyCharm.font')} #{String(nodeData.font_id)}
            </span>
          )}
          {nodeData.background_set_id && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
              {t('imageWorkflows.nodes.luckyCharm.theme')} {String(nodeData.background_set_id)}
            </span>
          )}
        </div>
      </div>
    </BaseNode>
  );
};

export const LuckyCharmNode = memo(LuckyCharmNodeComponent);
export default LuckyCharmNode;
